import type * as api from '@gitbook/api-client';

import type { DiffChangeType } from '../Diff';
import { type AgentEditTarget, isEditInTarget } from './editTarget';

export type PageChange = {
    id: string;
    change: DiffChangeType;
    spaceId: string;
    changeRequestId?: string;
    page: api.ChangedRevisionPage;
    baseRevisionId: string;
};

/**
 * The pages the agent changed in the target the conversation is working in, and how many of each
 * kind of change.
 *
 * Filtered rather than listing everything the conversation ever touched: a conversation can be
 * pointed at another change request part-way through, and the pages it edited before that belong to
 * somewhere the user is no longer looking. With no target known, everything is listed.
 */
export function computeChanges(
    messages: api.AIMessage[],
    editTarget: AgentEditTarget | null
): { changes: PageChange[]; counts: Record<DiffChangeType, number> } {
    const changes = listChangedContent(messages).filter(
        (change) => !editTarget || isEditInTarget(change, editTarget)
    );
    const counts: Record<DiffChangeType, number> = {
        added: 0,
        modified: 0,
        deleted: 0,
    };

    changes.forEach((change) => {
        counts[change.change] += 1;
    });

    return {
        changes,
        counts,
    };
}

/**
 * Every page the agent changed, one entry per page and per place it was changed in.
 */
export function listChangedContent(messages: api.AIMessage[]): PageChange[] {
    const result: Map<string, PageChange> = new Map();

    messages.forEach((message) => {
        message.steps.forEach((step) => {
            step.toolCalls?.forEach((toolCall) => {
                switch (toolCall.tool) {
                    case 'createPage': {
                        const id = getChangedPageId(toolCall);
                        result.set(id, {
                            id,
                            change: 'added',
                            spaceId: toolCall.spaceId,
                            ...(toolCall.changeRequestId
                                ? { changeRequestId: toolCall.changeRequestId }
                                : {}),
                            page: toolCall.page,
                            baseRevisionId: toolCall.baseRevisionId,
                        });
                        break;
                    }
                    case 'renamePage':
                    case 'movePage':
                    case 'editPage': {
                        const id = getChangedPageId(toolCall);
                        const existing = result.get(id);
                        result.set(id, {
                            id,
                            change: existing?.change === 'added' ? 'added' : 'modified',
                            spaceId: toolCall.spaceId,
                            ...(toolCall.changeRequestId
                                ? { changeRequestId: toolCall.changeRequestId }
                                : {}),
                            page: toolCall.page,
                            baseRevisionId: existing?.baseRevisionId ?? toolCall.baseRevisionId,
                        });
                        break;
                    }
                    case 'deletePage': {
                        const id = getChangedPageId(toolCall);
                        result.set(id, {
                            id,
                            change: 'deleted',
                            spaceId: toolCall.spaceId,
                            ...(toolCall.changeRequestId
                                ? { changeRequestId: toolCall.changeRequestId }
                                : {}),
                            page: toolCall.page,
                            baseRevisionId: toolCall.baseRevisionId,
                        });
                        break;
                    }
                }
            });
        });
    });

    return Array.from(result.values());
}

/**
 * Identity of a changed page: the page, and the change request or space it was changed in.
 *
 * The change request is part of it because a page of a space keeps its id in every change request
 * of that space. Keyed on the page alone, a conversation that edited one page in two change
 * requests would keep only whichever it edited last — and the page would vanish from the tray as
 * soon as the conversation went back to the other one.
 */
function getChangedPageId(toolCall: {
    spaceId: string;
    changeRequestId?: string;
    page: api.ChangedRevisionPage;
}): string {
    return `${toolCall.spaceId}/${toolCall.changeRequestId ?? ''}/${toolCall.page.id}`;
}
